import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../../data";
import { useAction, useRueckfrage } from "../../../components/ui";
import { currentLang, studioText, t } from "../../../lib/sprache";
import { DEAL_STATUS_ORDER, ZWISCHENSTAND_VORSCHLAEGE, labelMap, statusDef } from "../kooperationen/model";
import type { StatusLabel, Substep } from "../kooperationen/typen";

/**
 * Eigene Zwischenstände der Agentur je Oberstand (K-526, 0394): z. B. „Ware beim Creator“ unter „Produktion“. Sie erscheinen in
 * Liste und Detail der Kooperation unter dem Status, mit Datum; fachlich zählt weiter nur der Oberstand. Pflegen dürfen Chef und
 * Manager (Regeln der Tabelle). Archivieren statt löschen, solange eine Kooperation den Zwischenstand trägt, ist nicht nötig:
 * Löschen leert ihn dort (on delete set null).
 */
export function ZwischenstaendePanel({ orgId, labels }: { orgId: string; labels: readonly StatusLabel[] }) {
  const q = useQuery({
    queryKey: ["org_deal_substeps", orgId],
    queryFn: () => api.select<Substep & { org_id: string }>("org_deal_substeps", { filters: [["org_id", "eq", orgId]], order: [["sort", "asc"], ["label_de", "asc"]] }),
  });
  const { busy, error, run } = useAction();
  const rueckfrage = useRueckfrage();
  const own = labelMap([...labels]);
  const [neu, setNeu] = useState<Record<string, string>>({});
  const alle = q.data ?? [];
  const label = (s: Substep) => (currentLang() === "en" && s.label_en?.trim()) || s.label_de;

  const anlegen = (status: string, de: string, en: string | null) => run(`neu-${status}`, async () => {
    const sort = Math.max(0, ...alle.filter((x) => x.parent_status === status).map((x) => x.sort)) + 10;
    await api.insert("org_deal_substeps", { org_id: orgId, parent_status: status, label_de: de.trim(), label_en: en?.trim() || null, sort });
    setNeu((x) => ({ ...x, [status]: "" }));
  }, t("Zwischenstand angelegt.", "Intermediate step added."));
  // K-559 f: Reihenfolge je Status ändern (Tausch der Sortierwerte mit dem Nachbarn)
  const verschieben = (status: string, s: Substep, richtung: -1 | 1) => {
    const liste = alle.filter((x) => x.parent_status === status).sort((a, b) => a.sort - b.sort || a.label_de.localeCompare(b.label_de, "de"));
    const i = liste.findIndex((x) => x.id === s.id);
    const n = liste[i + richtung];
    if (!n) return;
    // gleiche Sortierwerte (ältere Einträge) erst auseinanderziehen
    const a = s.sort === n.sort ? (richtung === 1 ? n.sort + 1 : n.sort - 1) : n.sort;
    void run(`sort-${s.id}`, async () => {
      await api.update("org_deal_substeps", [["id", "eq", s.id]], { sort: a });
      await api.update("org_deal_substeps", [["id", "eq", n.id]], { sort: s.sort });
    });
  };
  const loeschen = async (s: Substep) => {
    if (!(await rueckfrage.ask({ title: t("Zwischenstand löschen?", "Delete intermediate step?"), confirmLabel: t("Löschen", "Delete"), danger: true,
      text: t(`„${label(s)}“ verschwindet aus der Auswahl. Kooperationen, die gerade diesen Zwischenstand haben, behalten ihren Status, der Zwischenstand fällt dort weg.`,
        `“${label(s)}” disappears from the selection. Deals that currently have this step keep their status; the step is removed there.`) }))) return;
    void run(`weg-${s.id}`, () => api.remove("org_deal_substeps", [["id", "eq", s.id]]), t("Zwischenstand gelöscht.", "Intermediate step deleted."));
  };

  return (
    <section className="panel stack" aria-labelledby="zwischen-title">
      {rueckfrage.element}
      <details>
        <summary id="zwischen-title">{t("Zwischenstände", "Intermediate steps")}</summary>
        <div className="stack">
          <p>{t("Eigene Arbeitsschritte unter einem Status, zum Beispiel „Ware beim Creator“ unter Produktion oder „Marke pausiert“ unter Blockiert. Du wählst sie im Status-Dialog der Kooperation, mit Datum. Sie erscheinen in Liste und Detail; Kennzahlen, Tafel und Abfolge richten sich weiter nach dem Status.",
            "Your own working steps under a status, for example “Product with creator” under production or “Brand paused” under blocked. You choose them in the deal's status dialog, with a date. They appear in the list and detail; metrics, board and order still follow the status.")}</p>
          {q.isPending ? <p className="muted">{t("Wird geladen …", "Loading …")}</p> : (
            <div className="tk-zwischen-liste">
              {DEAL_STATUS_ORDER.map((status) => {
                const eigene = alle.filter((x) => x.parent_status === status).sort((a, b) => a.sort - b.sort || a.label_de.localeCompare(b.label_de, "de"));
                const vorschlaege = ZWISCHENSTAND_VORSCHLAEGE.filter((v) => v.status === status && !eigene.some((e) => e.label_de.trim().toLowerCase() === studioText(v.de).toLowerCase()));
                return (
                  <div key={status} className="tk-zwischen-status">
                    <strong>{statusDef(status, own).label}</strong>
                    <div className="tk-chips">
                      {eigene.map((s, i) => (
                        <span key={s.id} className="pill tk-zwischen-chip">
                          {eigene.length > 1 && <button type="button" className="quiet" disabled={!!busy || i === 0} aria-label={t(`${label(s)} nach vorne`, `Move ${label(s)} up`)} onClick={() => verschieben(status, s, -1)}>‹</button>}
                          {label(s)}
                          {eigene.length > 1 && <button type="button" className="quiet" disabled={!!busy || i === eigene.length - 1} aria-label={t(`${label(s)} nach hinten`, `Move ${label(s)} down`)} onClick={() => verschieben(status, s, 1)}>›</button>}
                          <button type="button" className="quiet" disabled={!!busy} aria-label={t(`${label(s)} löschen`, `Delete ${label(s)}`)} onClick={() => void loeschen(s)}>×</button>
                        </span>
                      ))}
                      {vorschlaege.map((v) => (
                        <button key={v.de} type="button" className="tk-zwischen-vorschlag" disabled={!!busy} onClick={() => void anlegen(status, studioText(v.de), studioText(v.en))}>{t(`+ ${v.de}`, `+ ${v.en}`)}</button>
                      ))}
                    </div>
                    <form className="row" onSubmit={(e) => { e.preventDefault(); if ((neu[status] ?? "").trim()) void anlegen(status, neu[status]!, null); }}>
                      <label className="sr-only" htmlFor={`zwischen-${status}`}>{t(`Neuer Zwischenstand unter ${statusDef(status, own).label}`, `New step under ${statusDef(status, own).label}`)}</label>
                      <input id={`zwischen-${status}`} value={neu[status] ?? ""} maxLength={60} placeholder={t("Eigener Zwischenstand …", "Own step …")} onChange={(e) => setNeu((x) => ({ ...x, [status]: e.target.value }))} />
                      <button type="submit" disabled={!!busy || !(neu[status] ?? "").trim()}>{t("Hinzufügen", "Add")}</button>
                    </form>
                  </div>
                );
              })}
            </div>
          )}
          {error && <p className="tk-error" role="alert">{error}</p>}
        </div>
      </details>
    </section>
  );
}
